import clsx from 'clsx'
import { updateHtmlNodes } from 'components/form_builder/features/fields/fieldsSlice'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import {
  extractAllSections,
  findAndInsertNode,
  findAndRemoveQuestionFromPath,
  findQuestion,
  findSectionFromField,
} from '../../utility'
import { OverrideWarning } from './PathForMultipleFields'
import SectionDivider from '../SectionDivider'

const styles = {
  trigger: {
    padding: '3px 12px',
    border: '1px solid #B7B7B7',
    borderRadius: 6,
    background: 'var(--accent, #F0F0F0)',
    cursor: 'pointer',
  },
  dropdown: {
    position: 'absolute',
    width: '100%',
    background: 'white',
    padding: '0px 12px',
    zIndex: 1000,
    borderRadius: 6,
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
  },
  listItem: {
    padding: '6px 0px',
    borderBottom: '1px solid #DBDBDB',
    cursor: 'pointer',
  },
}

function SectionDropdown({ sections, activeSection, onSelect, onAddNew }) {
  const [openSectionInput, setOpenSectionInput] = useState(false)

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.code === 'KeyS') {
      e.preventDefault()
      e.target.blur()
    }
    if (e.code === 'Enter') e.target.blur()
  }

  return (
    <div style={styles.dropdown}>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} className="dome-d-flex dome-flex-column">
        <li onClick={() => onSelect(null)} style={styles.listItem}>
          <div className="dome-d-flex dome-gap6">
            <span className={clsx('dome-p2', activeSection ? 'dome-color-med-grey' : 'dome-color-darkest-grey')}>
              unassigned section
            </span>
            <span className="dome-p2 dome-color-med-grey">(default)</span>
          </div>
        </li>
        {sections.map((section) => {
          const active = activeSection?.id === section.id
          return (
            <li key={section.id} onClick={() => onSelect(section)} style={styles.listItem}>
              <span className={clsx('dome-p2', active ? 'dome-color-darkest-grey' : 'dome-color-med-grey')}>
                {section.section_attributes?.label}
              </span>
            </li>
          )
        })}
        {openSectionInput && (
          <li style={styles.listItem}>
            <input
              type="text"
              style={{ border: 'none', outline: 'none', width: '100%' }}
              onKeyDown={handleKeyDown}
              onBlur={(e) => {
                setOpenSectionInput(false)
                onAddNew(e.target.value)
              }}
              placeholder="name your new section"
              autoFocus
            />
          </li>
        )}
        <li
          onClick={() => setOpenSectionInput(true)}
          style={{ padding: '6px 0px' }}
          className="dome-p2 dome-color-aqua dome-text-w500 dome-cursor-pointer"
        >
          + new section
        </li>
      </ul>
    </div>
  )
}

function SectionTrigger({ activeSection, onClick }) {
  return (
    <div style={styles.trigger} className="dome-d-flex dome-align-center" onClick={onClick}>
      <div className="dome-flex-1">
        {activeSection ? (
          <span className="dome-p2 dome-color-darkest-grey">{activeSection.section_attributes?.label}</span>
        ) : (
          <span className="dome-p2 dome-color-med-grey">unassigned</span>
        )}
      </div>
      <i className="fa-regular fa-chevron-down dome-color-dark-grey"></i>
    </div>
  )
}

export default function GroupSectionAssignment({ components }) {
  const dispatch = useDispatch()
  const ref = useRef()
  const [openSectionMenu, setOpenSectionMenu] = useState(false)
  const [hasOverridden, setHasOverridden] = useState(false)

  const data = useSelector((state) => state.data)
  const { html_nodes, field_defs } = useSelector((state) => state.fields)
  const sections = useMemo(() => extractAllSections(html_nodes), [html_nodes])

  const allPaths = useMemo(
    () => [
      ...new Set(
        components.flatMap((component) => {
          const paths = data[component.type]?.[component.id]?.paths ?? []
          return paths.map((p) => p.path).filter(Boolean)
        })
      ),
    ],
    [components, data]
  )

  const pathSections = useMemo(
    () => allPaths.map((path) => findSectionFromField(html_nodes, path)),
    [allPaths, html_nodes]
  )

  const isUniform = useMemo(() => {
    if (pathSections.length === 0) return true
    const firstId = pathSections[0]?.id ?? null
    return pathSections.every((s) => (s?.id ?? null) === firstId)
  }, [pathSections])

  const isEditable = isUniform || hasOverridden
  const activeSection = isUniform ? pathSections[0] : null

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpenSectionMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const assignSectionToAllPaths = (section) => {
    const newHtmlNodes = structuredClone(html_nodes)
    allPaths.forEach((path) => findAndRemoveQuestionFromPath(newHtmlNodes, path))

    if (section) {
      const targetSection = findQuestion(newHtmlNodes, section.id)
      const insertIndex = targetSection?.nodes?.length ?? 0
      allPaths.forEach((path) => {
        const field = field_defs[path]
        const question = {
          type: 'question',
          question_attributes: {
            field: path,
            label: field?.label ?? '',
            question_label: field?.question_label ?? '',
          },
          nodes: [],
          id: uuidv4(),
        }
        findAndInsertNode(newHtmlNodes, section.id, question, insertIndex)
      })
    }

    dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
    setOpenSectionMenu(false)
  }

  const addNewSection = (value) => {
    if (!value?.trim()) return
    const newSection = {
      type: 'section',
      section_attributes: { label: value.trim() },
      nodes: [],
      id: uuidv4(),
    }
    const newHtmlNodes = structuredClone(html_nodes)
    newHtmlNodes.push(newSection)
    dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
  }

  const handleOverride = () => {
    setHasOverridden(true)
    setOpenSectionMenu(true)
  }

  return (
    <SectionDivider sectionName="Section">
      <div className="dome-d-flex dome-flex-column dome-gap6">
        <span className="dome-p3 dome-color-med-grey">
          {allPaths.length === 0 ? 'There is no path to assign to section' : 'section (all selected fields)'}
        </span>
        <div ref={ref} style={{ position: 'relative' }}>
          {allPaths.length === 0 ? null : isEditable ? (
            <>
              <SectionTrigger activeSection={activeSection} onClick={() => setOpenSectionMenu((prev) => !prev)} />
              {openSectionMenu && (
                <SectionDropdown
                  sections={sections}
                  activeSection={activeSection}
                  onSelect={assignSectionToAllPaths}
                  onAddNew={addNewSection}
                />
              )}
            </>
          ) : (
            <OverrideWarning onOverride={handleOverride} />
          )}
        </div>
      </div>
    </SectionDivider>
  )
}
